import { useId, type ReactNode } from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * FormField — label + control + description + error, wired together.
 *
 * The wiring is the point (05 §6.4): the label's `htmlFor`, the control's
 * `aria-describedby` and `aria-invalid`, and an error message that is announced
 * when it appears. Pass a function as `children` to receive the generated ids:
 *
 * ```tsx
 * <FormField label={t('cohorts.name')} error={errors.name}>
 *   {({ id, describedBy, invalid }) => (
 *     <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} … />
 *   )}
 * </FormField>
 * ```
 *
 * A plain node also works when the control already carries a matching `id`.
 */
export interface FormFieldRenderProps {
  /** Put this on the control; the label already points at it. */
  id: string;
  /** `aria-describedby` value covering the description and the error. */
  describedBy: string | undefined;
  /** `aria-invalid` value. */
  invalid: boolean;
}

export interface FormFieldProps {
  label: ReactNode;
  children: ReactNode | ((props: FormFieldRenderProps) => ReactNode);
  /** Helper text under the control. */
  description?: ReactNode;
  /** Message from validation or the API; presence also marks the field invalid. */
  error?: ReactNode;
  /** Adds the required marker and the `aria-required` hint to the label. */
  required?: boolean;
  /** Override the generated id when the control needs a stable one (tests). */
  htmlFor?: string;
  className?: string;
}

export function FormField({
  label,
  children,
  description,
  error,
  required = false,
  htmlFor,
  className,
}: FormFieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ms-1 text-destructive">
            *
          </span>
        ) : null}
      </Label>

      {typeof children === 'function'
        ? children({ id, describedBy, invalid: Boolean(error) })
        : children}

      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
